import { shallowRef } from 'vue'

/**
 * Scratch to win: what this browser remembers about the game.
 *
 * Two questions only — "may a card open by itself right now?" and "which step
 * does this visitor resume at?" — answered from timestamps, per card id (= gift
 * rule id). No e-mail address, phone number or prize code is ever written to
 * web storage. The lead token is (ledger S): it authorizes only setting a phone
 * once on a lead that has none, and asking for a resend.
 *
 * The pure half (everything in the first section) takes the store as an
 * argument and returns a new one, so it runs under plain Vitest with no Nuxt
 * runtime; only `vue`'s `shallowRef` is imported, for the write counter that
 * makes computeds reading the store re-evaluate.
 *
 * Every storage access is wrapped: hardened profiles and Safari's private mode
 * throw on `localStorage`, and the module-level mirrors below keep the game
 * working for that whole population — treating a storage failure as "already
 * answered" is what the old newsletter popup did, and it hid the offer from
 * them entirely.
 */

export type ScratchFlowStep = 'SCRATCH' | 'REVEAL' | 'EMAIL' | 'PHONE' | 'SUCCESS'
export type ScratchEmailNotice = 'WELCOME_BACK' | 'RESEND' | null

export const SCRATCH_STORE_KEY = 'scratch-card:v1'
export const SCRATCH_LEAD_KEY = 'scratch-card:lead'
/** sessionStorage: page loads in this tab session, so the first one can be told apart. */
export const SCRATCH_VIEWS_KEY = 'scratch-card:views'
/**
 * The old newsletter popup's answer key, still read to carry a dismissal over.
 */
export const LEGACY_NEWSLETTER_ANSWER_KEY = 'newsletter-modal-answer'
export const LEGACY_CARD_ID = 'legacy-newsletter'
/**
 * How long a dismissal buys. Closing the offer is not the same statement as
 * declining it forever — the newsletter popup's own number, kept.
 */
export const SCRATCH_DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000
export const SCRATCH_LEAD_TTL_MS = 30 * 24 * 60 * 60 * 1000
export const SCRATCH_MAX_CARDS = 10

export interface ScratchCardProgress {
  /** Latest AUTOMATIC opening (TIMED / EXIT_INTENT); overwritten. */
  promptedAt?: number
  revealedAt?: number
  emailDoneAt?: number
  /** A ScratchProgress with codeSent: true — this browser holds a code. */
  codeSentAt?: number
  phoneDoneAt?: number
  phoneSkippedAt?: number
  /** SUCCESS was reached. */
  completedAt?: number
  /** Latest plain close; overwritten. */
  dismissedAt?: number
}

export interface ScratchStore {
  v: 1
  /** Keyed by card id = gift rule id. */
  cards: Record<string, ScratchCardProgress>
  lastDismissedAt?: number
  /** May be LEGACY_CARD_ID. */
  lastDismissedCardId?: string
  /** ALREADY_PLAYED was answered in this browser. */
  playedAt?: number
}

export interface StoredLeadToken { leadToken: string, cardId: string, at: number }

/** The set-once fields: written only while still undefined. */
const SET_ONCE_FIELDS: readonly (keyof ScratchCardProgress)[] = [
  'revealedAt',
  'emailDoneAt',
  'codeSentAt',
  'phoneDoneAt',
  'phoneSkippedAt',
  'completedAt',
]

const PROGRESS_FIELDS: readonly (keyof ScratchCardProgress)[] = [
  'promptedAt',
  ...SET_ONCE_FIELDS,
  'dismissedAt',
]

// ---- Pure -------------------------------------------------------------------

export function emptyScratchStore(): ScratchStore {
  return { v: 1, cards: {} }
}

function finiteTimestamp(value: unknown): number | undefined {
  const at = typeof value === 'number' ? value : Number.NaN
  return Number.isFinite(at) && at > 0 ? at : undefined
}

/**
 * Tolerant by design: this reads whatever a previous version, another tab or a
 * hand-edited profile left behind, and anything it does not recognise is simply
 * not there. A shape it cannot vouch for must never silence the game.
 */
export function parseScratchStore(raw: string | null | undefined): ScratchStore {
  if (!raw)
    return emptyScratchStore()

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  }
  catch {
    return emptyScratchStore()
  }

  const source = parsed as Partial<ScratchStore> | null
  if (!source || source.v !== 1 || typeof source.cards !== 'object' || source.cards === null || Array.isArray(source.cards))
    return emptyScratchStore()

  const store = emptyScratchStore()

  for (const [cardId, value] of Object.entries(source.cards as Record<string, unknown>)) {
    if (!cardId || typeof value !== 'object' || value === null)
      continue
    const progress: ScratchCardProgress = {}
    for (const field of PROGRESS_FIELDS) {
      const at = finiteTimestamp((value as Record<string, unknown>)[field])
      if (at !== undefined)
        progress[field] = at
    }
    if (Object.keys(progress).length)
      store.cards[cardId] = progress
  }

  const lastDismissedAt = finiteTimestamp(source.lastDismissedAt)
  if (lastDismissedAt !== undefined)
    store.lastDismissedAt = lastDismissedAt
  if (typeof source.lastDismissedCardId === 'string' && source.lastDismissedCardId !== '')
    store.lastDismissedCardId = source.lastDismissedCardId
  const playedAt = finiteTimestamp(source.playedAt)
  if (playedAt !== undefined)
    store.playedAt = playedAt

  return store
}

/**
 * The newsletter popup's answer, read for its dismissal timestamp only.
 * `'subscribed'` is ignored: it carries no address, and the popup before the
 * scratcher overwrote it with `'dismissed'` on every close, so older answers
 * say nothing reliable about it.
 */
export function parseLegacyDismissedAt(raw: string | null): number | undefined {
  if (!raw)
    return undefined
  try {
    const parsed = JSON.parse(raw) as { kind?: unknown, at?: unknown } | null
    if (parsed?.kind !== 'dismissed')
      return undefined
    return finiteTimestamp(parsed.at)
  }
  catch {
    return undefined
  }
}

/**
 * Carries the old popup's silence over, as a dismissal of a card that does not
 * exist. The first real scratch card may therefore still prompt once inside it
 * (the NEW-card rule of `canAutoPrompt`), which is the point: a visitor who
 * closed a newsletter offer has not seen this game at all.
 */
export function migrateLegacyDismissal(store: ScratchStore, legacyAt: number | undefined): ScratchStore {
  if (!legacyAt || legacyAt <= (store.lastDismissedAt ?? 0))
    return store
  return { ...store, lastDismissedAt: legacyAt, lastDismissedCardId: LEGACY_CARD_ID }
}

/** The card entries worth keeping: the SCRATCH_MAX_CARDS most recently touched. */
function pruneCards(cards: Record<string, ScratchCardProgress>): Record<string, ScratchCardProgress> {
  const ids = Object.keys(cards)
  if (ids.length <= SCRATCH_MAX_CARDS)
    return cards
  const newest = (id: string) => Math.max(0, ...Object.values(cards[id] ?? {}).filter((at): at is number => typeof at === 'number'))
  const kept = ids.sort((a, b) => newest(b) - newest(a)).slice(0, SCRATCH_MAX_CARDS)
  return Object.fromEntries(kept.map(id => [id, cards[id]!]))
}

/**
 * Records progress on one card, immutably. `promptedAt` and `dismissedAt` are
 * the two overwritten fields (the latest automatic opening, the latest close);
 * every other field is set once, so a resumed flow cannot rewrite the moment it
 * first revealed or first had a code. A dismissal is also recorded globally,
 * which is what silences OTHER cards for the TTL.
 */
export function markCard(
  store: ScratchStore,
  cardId: string,
  fields: readonly (keyof ScratchCardProgress)[],
  now: number,
): ScratchStore {
  if (!cardId || !fields.length)
    return store

  const current = store.cards[cardId] ?? {}
  const progress: ScratchCardProgress = { ...current }
  for (const field of fields) {
    if (field === 'promptedAt' || field === 'dismissedAt' || progress[field] === undefined)
      progress[field] = now
  }

  const next: ScratchStore = { ...store, cards: pruneCards({ ...store.cards, [cardId]: progress }) }
  if (fields.includes('dismissedAt')) {
    next.lastDismissedAt = now
    next.lastDismissedCardId = cardId
  }
  return next
}

/** ALREADY_PLAYED came back: this browser is out of the game for every card. */
export function markPlayed(store: ScratchStore, now: number): ScratchStore {
  return store.playedAt ? store : { ...store, playedAt: now }
}

/** Nothing may open by itself any more, whatever the card. */
export function storeBlocksAllCards(store: ScratchStore): boolean {
  return !!store.playedAt || Object.values(store.cards).some(card => card.completedAt)
}

/**
 * May this card open unprompted right now?
 *
 * Three silences, in order: the game is over for this browser; this browser
 * already holds a code (from any card — one code is all anyone gets); this
 * card's own 30-day dismissal. A dismissal of a DIFFERENT card leaves a new
 * card exactly one opening inside that silence — closing one offer should not
 * hide a campaign that starts next week, but it should not be free either.
 */
export function canAutoPrompt(store: ScratchStore, cardId: string, now: number): boolean {
  if (storeBlocksAllCards(store))
    return false
  if (Object.entries(store.cards).some(([id, card]) => id !== cardId && card.codeSentAt))
    return false

  const card = store.cards[cardId] ?? {}
  if (card.dismissedAt && now - card.dismissedAt < SCRATCH_DISMISS_TTL_MS)
    return false

  if (store.lastDismissedAt
    && now - store.lastDismissedAt < SCRATCH_DISMISS_TTL_MS
    && store.lastDismissedCardId !== cardId) {
    return (card.promptedAt ?? 0) < store.lastDismissedAt
  }

  return true
}

/**
 * May the announcement strip open this card? The 30-day silence does not apply
 * — asking for the offer is not the same as being shown it — but a browser that
 * has finished a card or holds a code has nothing left to be offered.
 */
export function canManualOpen(store: ScratchStore, cardId: string): boolean {
  return !store.playedAt
    && !Object.entries(store.cards).some(([id, card]) => id !== cardId && (card.completedAt || card.codeSentAt))
}

/**
 * The step a returning visitor resumes at, and the line explaining why they are
 * back on the e-mail step.
 *
 * `hasLeadToken` decides the anonymous half-finished case: with a token the
 * phone can still be attached to the lead started here, without one the only
 * way on is to prove the e-mail again — unless the code was already sent (an
 * optional-phone card), in which case there is nothing left to do at all.
 */
export function resolveStartStep(
  store: ScratchStore,
  cardId: string,
  opts: { skipEmail: boolean, hasLeadToken: boolean },
): { step: ScratchFlowStep, emailNotice: ScratchEmailNotice } {
  const card = store.cards[cardId] ?? {}

  if (card.completedAt)
    return { step: 'SUCCESS', emailNotice: null }

  if (card.emailDoneAt && !card.phoneDoneAt && !card.phoneSkippedAt) {
    if (opts.hasLeadToken)
      return { step: 'PHONE', emailNotice: null }
    if (card.codeSentAt)
      return { step: 'SUCCESS', emailNotice: null }
    return { step: 'EMAIL', emailNotice: 'WELCOME_BACK' }
  }

  if (opts.skipEmail && card.revealedAt)
    return { step: 'PHONE', emailNotice: null }
  if (card.revealedAt)
    return { step: 'REVEAL', emailNotice: null }
  return { step: 'SCRATCH', emailNotice: null }
}

export function parseStoredLeadToken(raw: string | null, now: number): StoredLeadToken | null {
  if (!raw)
    return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredLeadToken> | null
    const at = finiteTimestamp(parsed?.at)
    if (!parsed
      || typeof parsed.leadToken !== 'string' || parsed.leadToken === ''
      || typeof parsed.cardId !== 'string' || parsed.cardId === ''
      || at === undefined
      || now - at > SCRATCH_LEAD_TTL_MS) {
      return null
    }
    return { leadToken: parsed.leadToken, cardId: parsed.cardId, at }
  }
  catch {
    return null
  }
}

// ---- Storage I/O (client only) ----------------------------------------------

/**
 * Mirrors for browsers that refuse `localStorage`. Module scope, so they
 * survive client-side navigation and the game still behaves sensibly for that
 * whole page life.
 */
let memoryStore: ScratchStore | null = null
let memoryLead: StoredLeadToken | null = null

/**
 * The newsletter answer is migrated at most once per page load.
 *
 * `readScratchStore()` is called from computeds that also read
 * `scratchStoreVersion`, and a migration that changes the store WRITES. Without
 * this flag every read would repeat that write, bump the version, invalidate the
 * computed that just called it, and loop. `migrateLegacyDismissal` is
 * idempotent, so the next page load simply reads the key again.
 */
let legacyMigrationAttempted = false

/**
 * Bumped by every write, successful or in-memory only. Computeds that read
 * `readScratchStore()` touch this ref so they re-evaluate — the store itself is
 * plain JSON in another tab's storage and is not reactive.
 */
export const scratchStoreVersion = shallowRef(0)

export function readScratchStore(): ScratchStore {
  if (!import.meta.client)
    return emptyScratchStore()

  let store: ScratchStore | null = null
  let legacyRaw: string | null = null
  try {
    const raw = window.localStorage.getItem(SCRATCH_STORE_KEY)
    if (raw)
      store = parseScratchStore(raw)
    if (!legacyMigrationAttempted)
      legacyRaw = window.localStorage.getItem(LEGACY_NEWSLETTER_ANSWER_KEY)
  }
  catch {
    // Unreadable — the in-memory mirror below carries this page life.
  }

  store ??= memoryStore ?? emptyScratchStore()

  if (legacyRaw !== null) {
    legacyMigrationAttempted = true
    const migrated = migrateLegacyDismissal(store, parseLegacyDismissedAt(legacyRaw))
    // Read, never removed: the newsletter popup is the fallback when no card is
    // active and needs its own answer back — deleting it re-offered the popup to
    // everyone who had dismissed it, and forgot every subscriber.
    if (migrated !== store) {
      store = migrated
      writeScratchStore(migrated)
    }
  }

  return store
}

/** False when the write only reached the in-memory mirror. */
export function writeScratchStore(store: ScratchStore): boolean {
  memoryStore = store
  scratchStoreVersion.value++
  if (!import.meta.client)
    return false
  try {
    window.localStorage.setItem(SCRATCH_STORE_KEY, JSON.stringify(store))
    return true
  }
  catch {
    return false
  }
}

export function readLeadToken(now: number = Date.now()): string | null {
  if (!import.meta.client)
    return null

  if (memoryLead && now - memoryLead.at <= SCRATCH_LEAD_TTL_MS)
    return memoryLead.leadToken

  try {
    const raw = window.localStorage.getItem(SCRATCH_LEAD_KEY)
    if (raw === null)
      return null
    const stored = parseStoredLeadToken(raw, now)
    if (!stored) {
      window.localStorage.removeItem(SCRATCH_LEAD_KEY)
      return null
    }
    memoryLead = stored
    return stored.leadToken
  }
  catch {
    return null
  }
}

export function writeLeadToken(leadToken: string, cardId: string, now: number = Date.now()): void {
  const stored: StoredLeadToken = { leadToken, cardId, at: now }
  memoryLead = stored
  if (!import.meta.client)
    return
  try {
    window.localStorage.setItem(SCRATCH_LEAD_KEY, JSON.stringify(stored))
  }
  catch {
    // The mirror above still carries this page life.
  }
}

export function clearLeadToken(): void {
  memoryLead = null
  if (!import.meta.client)
    return
  try {
    window.localStorage.removeItem(SCRATCH_LEAD_KEY)
  }
  catch {
    // Nothing to undo: the mirror is already cleared.
  }
}

/**
 * How many full page loads this tab session has had BEFORE this one, and counts
 * this one. `0` is therefore the landing page view — the one the SEO rule cares
 * about — and a storage failure answers 0 too, which errs towards the quieter
 * exit-intent behaviour.
 */
export function readAndCountPageView(): number {
  if (!import.meta.client)
    return 0
  try {
    const raw = window.sessionStorage.getItem(SCRATCH_VIEWS_KEY)
    const seen = Number.parseInt(raw ?? '', 10)
    const count = Number.isFinite(seen) && seen > 0 ? seen : 0
    window.sessionStorage.setItem(SCRATCH_VIEWS_KEY, String(count + 1))
    return count
  }
  catch {
    return 0
  }
}
